import { useState, type ComponentProps } from 'react'

import Badge from '@components/atoms/Badge'
import { MultiSelectField, type MultiSelectFieldOption } from '@components/molecules/MultiSelectField'
import cn from '@components/utils/cn'

import type { ComponentDoc, PropDocs } from '../types'

type MultiSelectFieldProps = ComponentProps<typeof MultiSelectField>

const props: PropDocs<MultiSelectFieldProps> = {
  name: { type: 'string', description: 'Field name, matched against errors. Defaults to a generated id.' },
  options: {
    type: 'MultiSelectFieldOption[]',
    required: true,
    description:
      'Each option is { value, label, description?, badge?, disabled?, hidden?, children? }. Search matches the label. Disabled options are locked and skipped by select all. Hidden options leave the list but keep their selection. Children show indented while their parent is selected.',
  },
  value: { type: 'string[]', required: true, description: 'The selected values. Fully controlled.' },
  onValueChange: {
    type: '(value: string[]) => void',
    required: true,
    description: 'Called with the new selection.',
  },
  label: {
    type: 'ReactNode',
    required: true,
    description: 'Visible label. It names the field for screen readers.',
  },
  placeholder: { type: 'ReactNode', required: true, description: 'Shown when nothing is selected.' },
  description: { type: 'string', description: 'Hint under the field.' },
  errors: { type: 'FieldErrors', description: 'react-hook-form errors. The error for name shows under the field.' },
  required: { type: 'boolean', default: 'false', description: 'Adds “(required)” to the label.' },
  hideLabel: { type: 'boolean', default: 'false', description: 'Keeps the label for screen readers only.' },
  searchPlaceholder: {
    type: 'string',
    required: true,
    description: 'Placeholder for the search box.',
  },
  noResultsText: { type: 'string', required: true, description: 'Shown when the search finds nothing.' },
  disabled: { type: 'boolean', default: 'false', description: 'Stops the dropdown from opening.' },
  listHeader: { type: 'ReactNode', description: 'Rendered between the search box and the list.' },
  showSelectAll: {
    type: 'boolean',
    default: 'false',
    description:
      'Adds a Select all row at the top of the list. Leave it off where selecting everything would be a mistake.',
  },
  showSelectedPills: {
    type: 'boolean',
    default: 'true',
    description: 'Shows the selected items as removable pills above the search box.',
  },
  selectedLabel: {
    type: '(selected: MultiSelectFieldOption[]) => ReactNode',
    description:
      'Replaces the "N selected" trigger text. Called with the selected options once at least one is selected; return nothing to show the placeholder instead.',
  },
}

const FRUIT: MultiSelectFieldOption[] = ['Apple', 'Banana', 'Cherry', 'Date', 'Elderberry', 'Fig', 'Grape', 'Honeydew'].map(
  (label) => ({ value: label.toLowerCase(), label })
)

const EVENTS: MultiSelectFieldOption[] = [
  { value: 'doorknock', label: 'Saturday doorknock', description: '21 Sep 2026' },
  {
    value: 'phonebank',
    label: 'Phone bank',
    description: '24 Sep 2026',
    badge: <Badge color="muted">Full</Badge>,
    disabled: true,
  },
  { value: 'stall', label: 'Market stall', description: '28 Sep 2026' },
]

type ControlledProps = Partial<MultiSelectFieldProps> & { initial?: string[] }

const PRODUCE = [
  { value: 'apple', label: 'Apple', group: 'fruit' },
  { value: 'banana', label: 'Banana', group: 'fruit' },
  { value: 'carrot', label: 'Carrot', group: 'veg' },
  { value: 'leek', label: 'Leek', group: 'veg' },
]

function WithListHeader() {
  const [group, setGroup] = useState<'fruit' | 'veg'>('fruit')
  const [value, setValue] = useState(['apple', 'carrot'])

  return (
    <div className="tw:w-full tw:max-w-md">
      <MultiSelectField
        options={PRODUCE.map((item) => ({ ...item, hidden: item.group !== group }))}
        value={value}
        onValueChange={setValue}
        label="Produce"
        placeholder="Choose produce"
        searchPlaceholder="Search produce"
        noResultsText="No produce matches"
        showSelectedPills={false}
        listHeader={
          <div role="group" aria-label="Group" className="tw:flex tw:gap-1 tw:px-3 tw:py-2">
            {(['fruit', 'veg'] as const).map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={group === option}
                onClick={() => setGroup(option)}
                className={cn(
                  'tw:rounded-md tw:px-2 tw:py-1 tw:text-sm',
                  group === option ? 'tw:bg-gray-100 tw:font-semibold tw:text-gray-900' : 'tw:text-gray-500'
                )}
              >
                {option === 'fruit' ? 'Fruit' : 'Veg'}
              </button>
            ))}
          </div>
        }
      />
    </div>
  )
}

function Controlled({ initial = [], options = FRUIT, ...rest }: ControlledProps) {
  const [value, setValue] = useState(initial)

  return (
    <div className="tw:w-full tw:max-w-md">
      <MultiSelectField
        name="fruit"
        options={options}
        value={value}
        onValueChange={setValue}
        label="Fruit"
        placeholder="Choose fruit"
        searchPlaceholder="Search fruit"
        noResultsText="No fruit matches"
        {...rest}
      />
    </div>
  )
}

const doc: ComponentDoc<MultiSelectFieldProps> = {
  slug: 'multi-select-field',
  name: 'MultiSelectField',
  tier: 'molecule',
  description:
    'Several choices from a list. The same label, hint and error as SelectField, and the trigger shows how many are selected. The popup has the selected pills, a search box, an optional select all and the options.',
  importStatement: "import { MultiSelectField } from '@components/molecules/MultiSelectField'",
  sourcePath: 'app/frontend/components/molecules/MultiSelectField.tsx',
  component: MultiSelectField,
  props,
  examples: [
    {
      title: 'Basic',
      render: () => <Controlled initial={['apple', 'cherry']} />,
      code: `const [value, setValue] = useState<string[]>([])

<MultiSelectField
  options={[{ value: 'apple', label: 'Apple' }, { value: 'banana', label: 'Banana' }]}
  value={value}
  onValueChange={setValue}
  label="Fruit"
  placeholder="Choose fruit"
  searchPlaceholder="Search fruit"
  noResultsText="No fruit matches"
/>`,
    },
    {
      title: 'Descriptions, badges and a disabled option',
      note: "A disabled option can't be toggled or removed. Select all and clear all skip it.",
      render: () => (
        <Controlled
          options={EVENTS}
          initial={['phonebank']}
          label="Events"
          placeholder="Choose events"
          searchPlaceholder="Search events"
          noResultsText="No events match"
          showSelectAll
        />
      ),
      code: `<MultiSelectField
  options={[
    { value: 'doorknock', label: 'Saturday doorknock', description: '21 Sep 2026' },
    { value: 'phonebank', label: 'Phone bank', description: '24 Sep 2026', badge: <Badge color="muted">Full</Badge>, disabled: true },
    { value: 'stall', label: 'Market stall', description: '28 Sep 2026' },
  ]}
  value={value}
  onValueChange={setValue}
  label="Events"
  placeholder="Choose events"
  searchPlaceholder="Search events"
  noResultsText="No events match"
  showSelectAll
/>`,
    },
    {
      title: 'List header, hidden options and no pills',
      note: 'The header sits between the search box and the list. Options hidden by the toggle leave the list and select all, but stay selected. This is the shape EventRsvpPicker uses.',
      render: () => <WithListHeader />,
      code: `const [group, setGroup] = useState<'fruit' | 'veg'>('fruit')

<MultiSelectField
  options={items.map((item) => ({ ...item, hidden: item.group !== group }))}
  value={value}
  onValueChange={setValue}
  showSelectedPills={false}
  listHeader={
    <div role="group" aria-label="Group" className="tw:flex tw:gap-1 tw:px-3 tw:py-2">
      <button type="button" aria-pressed={group === 'fruit'} onClick={() => setGroup('fruit')}>Fruit</button>
      <button type="button" aria-pressed={group === 'veg'} onClick={() => setGroup('veg')}>Veg</button>
    </div>
  }
  ...
/>`,
    },
    {
      title: 'Many selected',
      note: "After six pills the rest hide behind a … button. Clear all removes every pill that isn't locked.",
      render: () => <Controlled initial={FRUIT.map((option) => option.value)} />,
      code: `<MultiSelectField options={options} value={allEightValues} onValueChange={setValue} ... />`,
    },
    {
      title: 'With an error',
      render: () => (
        <Controlled initial={[]} required errors={{ fruit: { type: 'required', message: 'Choose at least one fruit' } }} />
      ),
      code: `<MultiSelectField name="fruit" errors={errors} required ... />`,
    },
    {
      title: 'Disabled',
      render: () => <Controlled initial={['banana']} disabled />,
      code: `<MultiSelectField ... disabled />`,
    },
  ],
  usage: {
    do: [
      'Use it whenever more than one option can be chosen. For one choice, use SelectField.',
      'Keep the selection in form state and pass it in as value.',
      "Use it inside a scrolling Dialog. The menu shrinks to the room the dialog has, so it is never cut off.",
      'Wrap it in a small adapter when the options come from data, like EventRsvpPicker.',
    ],
    dont: [
      'Map data to options in the page. Do that in the adapter.',
      'Turn on select all where one click could commit something irreversible, like attendance at every past event.',
      'Set select all yourself. It checks itself when every enabled option is selected.',
    ],
  },
  a11y: [
    'The label names the field. The hint and error are linked with aria-describedby.',
    'An error sets aria-invalid, and the border turns red.',
    'The trigger is a combobox linked to its label.',
    'Options and select all are checkable list items, so keyboard users reach them the same way.',
    'Buttons in listHeader need aria-pressed so their state is announced.',
    'Each pill\'s remove button is labelled "Remove {label}".',
  ],
}

export default doc
